import AsyncStorage from '@react-native-async-storage/async-storage';

import type { CalendarEvent } from '@/types/events';
import type { RouteObligation, RouteStopSource } from '@/types/route-plan';
import type { Task } from '@/types/tasks';
import { deviceCalendarService } from '@/lib/calendar/DeviceCalendarService';
import { RoutePlanStore } from '@/lib/route-plan/RoutePlanStore';

/**
 * A selectable stop candidate shown in the wizard's pick step.
 * `location` is the editable value; `originalLocation` tracks the value the
 * candidate started with so event write-backs only happen on real edits.
 */
export type RouteStopCandidate = {
  id: string;
  source: RouteStopSource;
  sourceId?: string;
  title: string;
  location: string;
  originalLocation: string;
  fixedStart?: string;
  fixedEnd?: string;
  estimatedDurationMinutes?: number;
  familyMemberIds: string[];
  sourceQuote?: string;
  selected: boolean;
};

const DEFAULT_TODO_DURATION_MINUTES = 10;

/**
 * RouteStopCandidateBuilder converts calendar events, scanned note
 * obligations, and open to-dos into wizard stop candidates.
 */
export class RouteStopCandidateBuilder {
  /**
   * Load the user's calendar events for a date (optionally clipped to a
   * time window), using the same selected-calendar behaviour as the rest
   * of the app.
   */
  static async loadDayEvents(
    date: string,
    windowStart: string | null,
    windowEnd: string | null,
  ): Promise<CalendarEvent[]> {
    let calendarIds: string[] = [];
    try {
      const savedSelection = await AsyncStorage.getItem('selectedCalendarIds');
      if (savedSelection) {
        calendarIds = JSON.parse(savedSelection);
      }
    } catch (storageError) {
      console.warn('[RouteStopCandidateBuilder] Error reading calendar IDs:', storageError);
    }

    if (calendarIds.length === 0) {
      const availableCalendars = await deviceCalendarService.getDeviceCalendars();
      calendarIds = availableCalendars.map((calendar) => calendar.id);
    }

    const dayStart = new Date(`${date}T00:00:00`);
    const dayEnd = new Date(`${date}T23:59:59`);
    const events = await deviceCalendarService.listEvents(calendarIds, dayStart, dayEnd);

    return events.filter((event) => {
      if (event.startDate !== date) return false;
      if (!event.startTime) return true; // all-day events stay visible
      if (windowStart && event.startTime < windowStart) return false;
      if (windowEnd && event.startTime > windowEnd) return false;
      return true;
    });
  }

  /**
   * Calendar event → candidate (default ticked).
   */
  static fromEvent(event: CalendarEvent): RouteStopCandidate {
    const location = event.location?.trim() ?? '';
    return {
      id: RoutePlanStore.generateId('cand'),
      source: 'event',
      sourceId: event.id,
      title: event.title,
      location,
      originalLocation: location,
      ...(event.startTime ? { fixedStart: event.startTime } : {}),
      ...(event.endTime ? { fixedEnd: event.endTime } : {}),
      familyMemberIds: [],
      selected: true,
    };
  }

  /**
   * Scanned note obligation → candidate (default ticked per spec).
   */
  static fromObligation(
    obligation: RouteObligation,
    rememberedLocation: string | null,
  ): RouteStopCandidate {
    const location = obligation.location?.trim() || rememberedLocation || '';
    return {
      id: RoutePlanStore.generateId('cand'),
      source: 'note',
      title: obligation.title,
      location,
      originalLocation: location,
      fixedStart: obligation.start,
      ...(obligation.end ? { fixedEnd: obligation.end } : {}),
      familyMemberIds: [obligation.familyMemberId],
      sourceQuote: obligation.sourceQuote,
      selected: true,
    };
  }

  /**
   * Open to-do → candidate (default unticked; the user opts errands in).
   */
  static fromTask(task: Task, rememberedLocation: string | null): RouteStopCandidate {
    const location = task.metadata?.location?.trim() || rememberedLocation || '';
    return {
      id: RoutePlanStore.generateId('cand'),
      source: 'todo',
      sourceId: task.id,
      title: task.title,
      location,
      originalLocation: location,
      estimatedDurationMinutes: DEFAULT_TODO_DURATION_MINUTES,
      familyMemberIds: [],
      selected: false,
    };
  }
}
